import React, { useState, useEffect } from 'react';
import { Check, Star, ArrowRight, Coffee } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NavigationHeader from '../components/NavigationHeader';
import planService from '../services/planService';
import { translateFeatures } from '../utils/featureTranslations';

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const result = await planService.getPublicPlans();
      if (result.success) {
        const sortedPlans = result.data
          .filter(plan => plan.active && plan.code !== 'TRIAL_FREE')
          .sort((a, b) => {
            // First sort by billing interval (monthly first)
            if (a.billingInterval !== b.billingInterval) {
              return a.billingInterval === 'MONTHLY' ? -1 : 1;
            }
            // Then by display order
            return (a.displayOrder || 0) - (b.displayOrder || 0);
          });
        setPlans(sortedPlans);
      }
    } catch (error) {
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (priceAmount, currency) => {
    const price = (priceAmount / 100).toFixed(2);
    const currencySymbol = currency === 'USD' ? '$' : currency;
    return `${currencySymbol}${price}`;
  };

  const getIntervalText = (interval) => {
    return interval === 'MONTHLY' 
      ? t('pricing.monthly', 'monthly')
      : t('pricing.yearly', 'yearly');
  };

  const renderFeatureList = (features) => {
    if (!features || features.length === 0) return null;
    
    // Get current language
    const currentLanguage = i18n.language || 'en';
    
    // Translate features to current language
    const translatedFeatures = translateFeatures(features, currentLanguage);
    
    return translatedFeatures.slice(0, 8).map((feature, index) => (
      <div key={index} className="flex items-start space-x-3 p-2 md:p-3 rounded-lg bg-gray-700/30">
        <Check className="w-4 h-4 md:w-5 md:h-5 text-green-400 flex-shrink-0 mt-0.5" />
        <span className="text-gray-200 text-sm md:text-base font-medium break-words leading-relaxed">
          {feature}
        </span>
      </div>
    ));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col justify-center items-center">
        <Coffee className="text-[#a15507] w-16 h-16 mb-6 animate-pulse" />
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#a15507]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <NavigationHeader />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('pricing.title', 'Simple, Transparent Pricing')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12">
              {t('pricing.subtitle', 'Choose the perfect plan for your coffee business. All plans include unlimited features to help you grow.')}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#a15507] border-t-transparent mx-auto mb-8"></div>
              <p className="text-gray-300 text-xl">{t('pricing.loading', 'Loading pricing plans...')}</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-xl">
                {t('pricing.noPlans', 'No pricing plans available at the moment.')}
              </p>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl w-full">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative bg-gray-800/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 border transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl ${
                      plan.popular
                        ? 'border-[#a15507] shadow-xl shadow-[#a15507]/20 ring-2 ring-[#a15507]/30'
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="bg-gradient-to-r from-[#a15507] to-[#d97706] text-white px-3 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-bold flex items-center space-x-1 shadow-lg whitespace-nowrap">
                          <Star className="w-3 h-3 md:w-4 md:h-4" />
                          <span>{t('pricing.popular', 'Most Popular')}</span>
                        </div>
                      </div>
                    )}

                    {plan.recommended && (
                      <div className="absolute top-4 right-4 z-10">
                        <div className="bg-green-500 text-white px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-bold whitespace-nowrap">
                          {t('pricing.recommended', 'Recommended')}
                        </div>
                      </div>
                    )}

                    <div className="text-center mb-6 md:mb-8">
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3 break-words">
                        {plan.name}
                      </h3>
                      <p className="text-gray-400 mb-4 md:mb-6 leading-relaxed text-sm md:text-base break-words">
                        {plan.description}
                      </p>
                      
                      <div className="mb-4 md:mb-6">
                        <span className="text-3xl md:text-5xl font-bold text-white">
                          {formatPrice(plan.priceAmount, plan.currency)}
                        </span>
                        <span className="text-gray-400 text-base md:text-lg block mt-1">
                          /{getIntervalText(plan.billingInterval)}
                        </span>
                        {plan.billingInterval === 'YEARLY' && (
                          <div className="text-sm text-green-400 mt-2 font-medium">
                            {t('pricing.save', 'Save 16% vs monthly')}
                          </div>
                        )}
                      </div>

                      <Link
                        to={isAuthenticated ? "/dashboard" : "/register"}
                        className={`w-full py-3 md:py-4 px-4 md:px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 text-sm md:text-base ${
                          plan.popular
                            ? 'bg-gradient-to-r from-[#a15507] to-[#d97706] text-white hover:from-[#924e06] hover:to-[#b45309] shadow-lg'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        <span className="break-words">
                          {isAuthenticated 
                            ? t('pricing.managePlan', 'Manage Plan')
                            : t('pricing.getStarted', 'Get Started')
                          }
                        </span>
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                      </Link>
                    </div>

                    <div className="space-y-3 md:space-y-4">
                      <h4 className="text-base md:text-lg font-semibold text-white mb-3 md:mb-4 break-words">
                        {t('pricing.features', 'Features included:')}
                      </h4>
                      <div className="space-y-2 md:space-y-3">
                        {renderFeatureList(plan.features)}
                      </div>
                      
                      {plan.features && plan.features.length > 8 && (
                        <div className="text-center pt-3 md:pt-4">
                          <span className="text-[#a15507] text-xs md:text-sm font-medium bg-gray-700/40 px-2 md:px-3 py-1 rounded-full whitespace-nowrap">
                            {t('pricing.moreFeatures', `+${plan.features.length - 8} more features`)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center bg-gray-800/40 backdrop-blur-sm rounded-2xl p-8 border border-gray-600">
            <h3 className="text-2xl font-bold text-white mb-4">
              {t('pricing.trialTitle', 'Start Your Free Trial Today')}
            </h3>
            <p className="text-gray-300 mb-6 text-lg">
              {t('pricing.trialText', 'Start with a free trial - no credit card required')}
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg text-gray-300 mb-8">
              <span className="flex items-center bg-gray-700/40 px-4 py-2 rounded-full">
                <Check className="w-5 h-5 text-green-400 mr-2" />
                {t('pricing.noSetupFee', 'No setup fees')}
              </span>
              <span className="flex items-center bg-gray-700/40 px-4 py-2 rounded-full">
                <Check className="w-5 h-5 text-green-400 mr-2" />
                {t('pricing.cancelAnytime', 'Cancel anytime')}
              </span>
              <span className="flex items-center bg-gray-700/40 px-4 py-2 rounded-full">
                <Check className="w-5 h-5 text-green-400 mr-2" />
                {t('pricing.support', '24/7 support')}
              </span>
            </div>
            
            {!isAuthenticated && (
              <Link
                to="/register"
                className="inline-flex items-center space-x-2 bg-[#a15507] hover:bg-[#924e06] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg"
              >
                <span>{t('pricing.startTrial', 'Start Free Trial')}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
